import React from "react";

import {
  Dialog,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from "@headlessui/react";
import { useState } from "react";

import { ChevronDownIcon } from "@heroicons/react/20/solid";
import {
  Bars3Icon,
  ShoppingCartIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import Logo from "@/components/svg/logo";
import { headerRoutes } from "@/data/routes/header";
import { Link } from "gatsby";
import { useContext } from "react";
import CartContext, { CartContextType } from "../../context/cart/CartContext";

export default function Example() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const cart = useContext<CartContextType>(CartContext);
  return (
    <header className="relative isolate z-10 bg-transparent">
      <nav
        aria-label="Global"
        className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8"
      >
        <div className="flex lg:flex-1">
          <Link to="/" className="-m-1.5 p-1.5">
            <span className="sr-only">Your Company</span>
            <Logo />
          </Link>
        </div>
        <div className="flex lg:hidden">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>
        </div>
        <PopoverGroup className="hidden lg:flex lg:gap-x-12">
          <Popover>
            <PopoverButton className="flex items-center gap-x-1 text-sm/6 font-semibold text-gray-900">
              {headerRoutes[0].title}
              <ChevronDownIcon
                aria-hidden="true"
                className="size-5 flex-none text-gray-400"
              />
            </PopoverButton>

            <PopoverPanel
              transition
              className="absolute inset-x-0 top-0 -z-10 bg-white pt-14 shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:-translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
            >
              <div className="mx-auto grid max-w-7xl grid-cols-3 gap-x-4 px-6 py-10 lg:px-8 xl:gap-x-8">
                {headerRoutes[0].subMenu?.map((item) => (
                  <div
                    key={item.name}
                    className="relative p-3 text-sm/6 text-fehr-500 font-semibold"
                  >
                    <p>{item.name}</p>
                    {item.links.map((link) => (
                      <Link
                        to={`/produits/${item.slug}/${link.slug}`}
                        className="mt-6 block font-semibold text-gray-900 hover:text-fehr-500"
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            </PopoverPanel>
          </Popover>
          {headerRoutes
            .filter((route) => route.isHeader && !route.dropdown)
            .map((route, index) => {
              return !route.external ? (
                <Link
                  key={index}
                  to={`/${route.slug}/`}
                  className="text-sm/6 font-semibold text-gray-900"
                >
                  {route.title}
                </Link>
              ) : (
                <a
                  href={route.slug}
                  target="_blank"
                  className="text-sm/6 font-semibold text-gray-900"
                >
                  {route.title}
                </a>
              );
            })}
        </PopoverGroup>
        <div className="hidden lg:flex lg:flex-1 lg:justify-end">
          <Link
            to="/panier"
            className="text-sm/6 font-semibold text-gray-900 flex items-center gap-x-2 border border-gray-200 rounded-lg p-3"
          >
            <ShoppingCartIcon className="size-5" /> Panier
            <span className="p-3 bg-gray-200 rounded-full h-7 w-7 flex items-center justify-center">
              {cart.itemCount}
            </span>{" "}
            <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>
      </nav>
      <Dialog
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
        className="lg:hidden"
      >
        <div className="fixed inset-0 z-10" />
        <DialogPanel className="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center justify-between">
            <a href="#" className="-m-1.5 p-1.5">
              <span className="sr-only">Your Company</span>
              <Logo />
            </a>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="-m-2.5 rounded-md p-2.5 text-gray-700"
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon aria-hidden="true" className="size-6" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-2 py-6">
                {headerRoutes[0].subMenu?.map((route) => (
                  <Disclosure as="div" className="-mx-3">
                    <DisclosureButton className="group flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base/7 font-semibold text-gray-900 hover:bg-gray-50">
                      {route.name}
                      <ChevronDownIcon
                        aria-hidden="true"
                        className="size-5 flex-none group-data-[open]:rotate-180"
                      />
                    </DisclosureButton>
                    <DisclosurePanel className="mt-2 space-y-2">
                      {route.links.map((item) => (
                        <Link
                          key={item.name}
                          to={`/produits/${route.slug}/${item.slug}`}
                          className="block rounded-lg py-2 pl-6 pr-3 text-sm/7 font-normal text-gray-900 hover:bg-gray-50"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </DisclosurePanel>
                  </Disclosure>
                ))}
                <a
                  href="https://fehrgroup.com/contact"
                  target="_blank"
                  className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                >
                  Contact
                </a>
              </div>
              <div className="py-2">
                <Link
                  to="/panier"
                  className="-mx-3 rounded-lg px-3 py-2.5 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 flex items-center gap-x-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Panier{" "}
                  <span className="text-sm rounded-full bg-fehr-500 text-white p-2 w-8 h-8 flex items-center justify-center">
                    {cart.itemCount}
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  );
}
